import { FunctionComponent } from "react";

import { AutoCompleteItem as DropdownItem, Dropdown } from "@/components/Dropdown/Dropdown";

import { MsdynamicsValueMap } from "../../schema/dynamics";
import { isCountryCodeInput, valueMapTargets } from "./valueMapDisplay";

type Ts = (key: string, options?: Record<string, unknown>) => string;

interface CountryValueMapEditorProps {
	mappingId: number;
	valueMap: MsdynamicsValueMap;
	options?: { label: string; value: string }[];
	onSave: (sourceKey: string, targetValue: string, targetLabel: string | null) => void;
	ts: Ts;
}

const NOTHING = "__nothing__";

/**
 * The edit view of a country map (PREC-4364): each mapped country can be pointed at another
 * option or record, or emptied; each record nothing was assigned to can be given a country code.
 */
export const CountryValueMapEditor: FunctionComponent<CountryValueMapEditorProps> = ({
	mappingId,
	valueMap,
	options,
	onSave,
	ts,
}) => {
	const targets = valueMapTargets(valueMap, options);
	const items: DropdownItem[] = [
		{ id: NOTHING, title: ts("valueMap.writeNothing") },
		...targets.map((target) => ({ id: target.value, title: target.label })),
	];

	return (
		<div
			className="flex flex-col gap-1 max-h-[240px] overflow-y-auto"
			data-test={`dynamics-country-editor-${mappingId}`}
		>
			{valueMap.target !== "TEXT" &&
				valueMap.entries.map((entry) => (
					<div key={entry.sourceKey} className="flex items-center gap-2">
						<span className="w-[40px] font-medium">{entry.sourceKey}</span>
						<div className="w-[260px]">
							<Dropdown
								data={items}
								defaultValue={items.find((item) => item.id === (entry.targetValue || NOTHING))}
								floating={true}
								handleSelect={(item) => {
									const target = targets.find((candidate) => candidate.value === item.id);

									onSave(
										entry.sourceKey,
										item.id === NOTHING ? "" : String(item.id),
										target?.label ?? null
									);
								}}
								label=""
							/>
						</div>
					</div>
				))}
			{(valueMap.unplaced ?? []).map((option) => (
				<div key={option.value} className="flex items-center gap-2">
					<span className="w-[260px] truncate">{option.label}</span>
					<input
						className="input-styles !py-1 !text-xs w-[70px]"
						data-test={`dynamics-country-code-${option.value}`}
						maxLength={2}
						placeholder={ts("valueMap.countryCode")}
						onBlur={(event) => {
							if (isCountryCodeInput(event.target.value)) {
								onSave(event.target.value.trim().toUpperCase(), option.value, option.label);
							}
						}}
					/>
				</div>
			))}
		</div>
	);
};
